//
// Folder scope reads as the inviter's own labels, resolved by /invite/sts: the
// invitee holds no account there and cannot name those folders itself.
//
import {
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {
  Column,
  GcatCodeIcon,
  Row,
  useWizardContext,
} from '../../../components';
import {
  useAuth,
  useTownAssets,
} from '../../../stores';
import {
  config,
  logError,
  privilegeDomains,
  privilegesToDomainState,
} from '../../../utils';

const pageStateValidator = () => ({ valid: {}, errors: {}, pageValid: true });

// The server's refusal flags, as the sentence shown for each
const refusalKeyOf = (data) => {
  if (data?.alreadyAssigned) return 'inviteAlreadyAssigned';
  if (data?.finalizeInProgress) return 'finalizeInProgress';
  if (data?.inviteEmailMismatch) return 'inviteEmailMismatch';
  if (data?.emailDomainClaimed) return 'finalizeAccountRefused';
  return 'finalizeError';
};

function InviteAckConfirmPage() {
  const { assets } = useTownAssets();
  const { translate } = assets;
  const { auth } = useAuth();
  const { superagentPostAsync } = auth;
  const {
    setPageInfo,
    setPageState,
    setPageBusy,
    goToPage,
    state,
  } = useWizardContext();

  // The acceptance reads the wizard as it is when clicked, not as it was when
  // this callback was registered
  const stateRef = useRef(state);
  stateRef.current = state;

  const confirmPage = useCallback(
    async () => {
      const {
        piid, fname, lname, email, rlDesc, grpGcat, grpLbl, grpDesc, teamSetup, onAccepted,
      } = stateRef.current;

      setPageBusy(true);
      setPageState({ refusalKey: undefined }, false);

      const url = config.town.back.urlPrefix
                + config.town.back.invite.finalize;

      let data;
      let accepted = false;

      try {
        // A signed-in invitee is named on the row; an anonymous one still passes
        const res = await superagentPostAsync(
          url,
          ['Content-Type', 'application/json'],
          {
            piid,
            fname: fname.trim(),
            lname: lname.trim(),
            email,
            rlDesc: rlDesc.trim(),
            grpGcat,
            grpLbl: grpLbl.trim(),
            grpDesc: grpDesc.trim(),
            teamSetup,
          },
        );

        // 200 with no data is the acceptance; any object is a refusal naming its
        // reason, so a new reason is never read as a success
        data = res?.body?.rslt?.data?.[0];
        accepted = (res?.status === 200) && (!data);
      } catch (err) {
        logError(
          `finalizeInvite ${err?.message ?? err}`,
          'InviteAckConfirmPage.finalize.error',
          { piid },
        );
      }

      if (accepted) {
        // The invite is consumed: the page behind must not offer it again
        onAccepted(email);
        goToPage('success');

        // The wizard reports rather than closes: there is nothing behind it
        return false;
      }

      setPageState({ refusalKey: refusalKeyOf(data) }, false);
      setPageBusy(false);

      return false;
    },
    [superagentPostAsync, setPageState, setPageBusy, goToPage],
  );

  useEffect(
    () => {
      setPageInfo({
        prevPage: 'teamSize',
        pageStateValidator,
        confirmPage,
      });
    },
    [setPageInfo, confirmPage],
  );

  const prtnrInvtInfo = state.invite?.prtnrInvtInfo;
  const privilegeState = privilegesToDomainState(prtnrInvtInfo?.privileges ?? []);
  const trgtFldrPaths = state.invite?.trgtFldrPaths ?? [];
  const ccatDomnCodes = prtnrInvtInfo?.target?.ccatDomnCodes ?? [];

  return (
    <Column crossStretch gap={6} className="flex-1 min-h-0 w-full overflow-y-auto p-2">
      {/* What the partnership grants */}
      <Column crossStart gap={1}>
        <span>
          {`${translate(
            'partnershipGrantsYou',
            'statements',
            {
              inviter: prtnrInvtInfo?.source?.cstmrLbl,
            },
          )}:`}
        </span>

        <span className="ml-4">
          {`${translate('type')}: `}
          <b>{translate(prtnrInvtInfo?.inviteType, 'inviteType')}</b>
        </span>

        <span className="ml-4">{`${translate('sectors')}:`}</span>
        {(trgtFldrPaths.length)
          ? trgtFldrPaths.map((folder) => (
              <span className="ml-8 font-semibold" key={folder.fldrId}>
                {folder.fldrPath}
              </span>
            ))
          : (
              <span className="ml-8 font-semibold">
                {translate('throughTheEntireSite', 'statements')}
              </span>
            )}

        {(ccatDomnCodes.length)
          ? (
              <>
                <span className="ml-4">{`${translate('missions')}:`}</span>
                <span className="ml-8 font-semibold">
                  {ccatDomnCodes
                    .map((code) => translate(code.replace('qr.', ''), 'userMainMenu'))
                    .join(', ')}
                </span>
              </>
            )
          : null}

        <span className="ml-4">{`${translate('authorizations')}:`}</span>
        {privilegeDomains
          .filter((domain) => (domain.domain !== 'partners'))
          .map((domain) => {
            const domainLevel = privilegeState[domain.domain];
            const levelIndex = domainLevel?.levelIndex ?? 0;
            const activeToggles = domain.toggles
              .filter((toggle) => (!!domainLevel?.toggles[toggle.code]));

            if ((levelIndex === 0) && (!activeToggles.length)) return null;

            const grants = [
              ...((levelIndex > 0)
                ? [translate(domain.levels[levelIndex].labelKey, 'privileges')]
                : []),
              ...activeToggles.map((toggle) => translate(toggle.labelKey, 'privileges')),
            ];

            return (
              <span className="ml-8" key={domain.domain}>
                {`${translate(domain.labelKey, 'privileges')}: `}
                <b>{grants.join(', ')}</b>
              </span>
            );
          })}
      </Column>

      {/* What is about to be created for it */}
      <Column crossStart gap={1}>
        <span>
          {`${translate(
            'aboutToCreateCstmrUserAccount',
            'statements',
            { cstmr: state.cstmrLbl },
          )}:`}
        </span>

        <Row className="ml-4 flex-wrap">
          <span className="font-semibold">{`${state.fname} ${state.lname},`}</span>
          <span className="font-semibold">{state.email}</span>
        </Row>

        <span className="ml-4 font-semibold italic">{state.rlDesc}</span>

        <span className="ml-4">{`${translate('memberOf', undefined, undefined, false)}:`}</span>

        <Row className="ml-4">
          <GcatCodeIcon gcatCode={state.grpGcat} className="h-6 w-6" />
          <span className="font-semibold">{state.grpLbl}</span>
        </Row>

        <span className="ml-8 font-semibold italic">{state.grpDesc}</span>
      </Column>

      {(state.refusalKey)
        ? (
            <span className="text-pxUiAlert italic">
              {translate(state.refusalKey, 'statements')}
            </span>
          )
        : null}
    </Column>
  );
}

export default InviteAckConfirmPage;
